/* eslint-disable no-console */
import {
  ApolloClient,
  ApolloLink,
  InMemoryCache,
  Observable,
  Operation,
  Reference,
  createHttpLink,
  from,
  split,
} from '@apollo/client';
import {
  ApolloCache,
  ModifierDetails,
  StoreObject,
} from '@apollo/client/cache';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { RetryLink } from '@apollo/client/link/retry';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import { DefinitionNode, Kind, OperationDefinitionNode } from 'graphql';
import { createClient } from 'graphql-ws';

import { AnalyticsTracker } from '@backstage/core-plugin-api';

import { GraphQLAppError } from '../GraphQLAppError';
import { Error as GraphQLErrorResult } from '../graphql';
import { deepOmit } from '../utils/deepOmit';
import { PageLoadTracker } from './PageLoadTracker';
import { createBackendWriteLink } from './backendWriteLink';
import {
  entityIdentityBindingsListFieldPolicy,
  metricsV1EntityIdentityBindingTypePolicy,
} from './listCache/entityIdentityBindingsListCachePolicy';
import { namedMerger } from './namedMerger';
import { operationQueueManager } from './operationQueue';
import { parseRetryAfter } from './parseRetryAfter';

const generateRequestId = () => {
  return Math.random().toString(36).substring(2) + Date.now().toString(36);
};

const BUILD_COMMIT_HASH = document.querySelector<HTMLMetaElement>(
  'meta[name="build-commit"]',
)?.content;

const pageLoadTracker = new PageLoadTracker();

export type GraphQLResult = { __typename?: string };
type GraphQLErrorResultWithInfo = GraphQLErrorResult & {
  errorInfoReason?: string | null;
  errorInfoDomain?: string | null;
};
type ValueOfTypename<T extends GraphQLResult> = T['__typename'];

export function isType<
  Result extends GraphQLResult,
  Typename extends ValueOfTypename<Result>,
>(
  result: Result | null | undefined,
  typename: Typename,
): result is Extract<Result, { __typename: Typename }> {
  return result?.__typename === typename;
}

export function getError<Result extends GraphQLResult>(
  result: Result | null | undefined,
): GraphQLAppError | null {
  if (isError(result)) {
    return new GraphQLAppError(
      result.message ??
        result.code?.replaceAll('_', ' ') ??
        'Something went wrong',
      result.code,
      {
        reason: result.errorInfoReason,
        domain: result.errorInfoDomain,
      },
    );
  }
  return null;
}

export function getFunctionError<Result extends GraphQLResult>(
  result: Result | null | undefined,
): Error | null {
  const error = getError(result);
  if (error) {
    return new Error(
      error.message.replace(/^Function failed \[/, '').replace(/\]$/, ''),
    );
  }

  return null;
}

export function getTypeOrNull<Result extends GraphQLResult>(
  result: Result | null | undefined,
): Extract<
  Exclude<Result, { __typename: 'Error' }>,
  { __typename: ValueOfTypename<Result> }
> | null {
  if (!result) {
    return null;
  }

  if (isError(result)) {
    return null;
  }

  if (!result.__typename) {
    return null;
  }

  return result as Extract<
    Exclude<Result, { __typename: 'Error' }>,
    { __typename: ValueOfTypename<Result> }
  >;
}

export const getTypeOrUndef = <T>(
  value: T | null | undefined,
): T | undefined => {
  if (!value || isError(value)) {
    return undefined;
  }

  return value;
};

export function isError<Result extends GraphQLResult>(
  result: Result | null | undefined,
): result is Extract<Result, GraphQLErrorResultWithInfo> {
  return result?.__typename === 'Error';
}

export const evictTypeFields = <T>(
  cache: ApolloCache<Record<string, StoreObject>>,
  field: keyof T,
) => {
  cache.modify({
    fields: {
      [field]: (_: unknown, { DELETE }: ModifierDetails) => {
        return DELETE;
      },
    },
  });
};

function isOperationDefinition(
  node: DefinitionNode,
): node is OperationDefinitionNode {
  return node.kind === Kind.OPERATION_DEFINITION;
}

const isOperationType = (
  operation: Operation,
  type: 'mutation' | 'subscription',
): boolean =>
  operation.query.definitions.some(
    def => isOperationDefinition(def) && def.operation === type,
  );

const DEFAULT_COOLDOWN_MS = 10_000;
const MIN_COOLDOWN_MS = 5_000;
const MAX_COOLDOWN_MS = 120_000;
const COOLDOWN_JITTER_MS = 1_000;
const RETRY_BASE_DELAY_MS = 500;

export const rateLimitState = { until: 0 };

export type CreateApolloClientOptions = {
  token?: string;
  uri?: string;
  region?: 'EU' | 'US';
  analyticsApi?: AnalyticsTracker;
  getAccessToken?: () => string | undefined;
  getAnalyticsApi?: () => AnalyticsTracker | undefined;
  onBackendWrite?: (operation: string) => void;
};

export const createApolloClient = ({
  token,
  uri,
  region,
  analyticsApi,
  getAccessToken,
  getAnalyticsApi,
  onBackendWrite,
}: CreateApolloClientOptions) => {
  const resolveToken = (): string | undefined =>
    getAccessToken ? getAccessToken() : token;
  const resolveAnalytics = (): AnalyticsTracker | undefined =>
    getAnalyticsApi ? getAnalyticsApi() : analyticsApi;

  if (!resolveToken() || !uri || !region) {
    return new ApolloClient({
      cache: new InMemoryCache(),
      link: operationQueueManager.createQueueLink(),
    });
  }

  const httpLink = createHttpLink({ uri });

  // Detect rapid reconnection loops and stop them
  const reconnectLimiter = {
    closeTimes: [] as number[],
    maxCloses: 20,
    windowMs: 5000,
    shouldStop: () => {
      const now = Date.now();
      // Remove old entries outside the window
      reconnectLimiter.closeTimes = reconnectLimiter.closeTimes.filter(
        t => now - t < reconnectLimiter.windowMs,
      );
      return reconnectLimiter.closeTimes.length >= reconnectLimiter.maxCloses;
    },
    recordClose: () => {
      reconnectLimiter.closeTimes.push(Date.now());
      // Keep array bounded - we only need maxCloses entries
      if (reconnectLimiter.closeTimes.length > reconnectLimiter.maxCloses) {
        reconnectLimiter.closeTimes.shift();
      }
    },
  };

  const wsClient = createClient({
    url: `wss://graphql-websocket.confidence.dev/websocket`,
    connectionParams: () => {
      const accessToken = resolveToken();
      return {
        authorization: accessToken ? `Bearer ${accessToken}` : '',
      };
    },
    on: {
      closed: () => {
        reconnectLimiter.recordClose();
        if (reconnectLimiter.shouldStop()) {
          // Too many closes in a short time - likely a persistent error.
          // Dispose to prevent infinite reconnection loop.
          wsClient.dispose();
        }
      },
    },
  });

  const wsLink = new GraphQLWsLink(wsClient);

  const omitTypenameLink = new ApolloLink((operation, forward) => {
    if (operation.variables) {
      operation.variables = deepOmit(operation.variables, '__typename');
    }
    return forward(operation);
  });

  const authLink = setContext(async (_, { headers }) => {
    const accessToken = resolveToken();
    return {
      headers: {
        ...headers,
        authorization: accessToken ? `Bearer ${accessToken}` : '',
      },
    };
  });

  const requestIdLink = new ApolloLink((operation, forward) => {
    const requestId = generateRequestId();
    operation.setContext({
      requestId,
      headers: {
        ...operation.getContext().headers,
        'x-confidence-request-id': requestId,
        'x-confidence-build-version': BUILD_COMMIT_HASH,
      },
    });
    return forward(operation);
  });

  const errorLink = onError(({ operation, graphQLErrors, networkError }) => {
    if (graphQLErrors)
      graphQLErrors.forEach(({ message, path }) =>
        console.error(`[GraphQL error]: Message: ${message}, Path: ${path}`),
      );

    if (networkError) {
      const definition = getMainDefinition(operation.query);
      const isSubscription =
        definition.kind === 'OperationDefinition' &&
        definition.operation === 'subscription';

      if (!isSubscription) {
        console.error(`[Network error]: ${networkError}`);

        const tracker = resolveAnalytics();
        if (tracker) {
          tracker.captureEvent('graphql-error', operation.operationName, {
            attributes: {
              networkError: networkError?.message ?? 'Unknown network error',
              requestId: operation.getContext().requestId,
            },
          });
        }
      }
    }
  });

  const rateLimitGateLink = new ApolloLink((operation, forward) => {
    if (
      isOperationType(operation, 'mutation') ||
      isOperationType(operation, 'subscription')
    ) {
      return forward(operation);
    }

    const remaining = rateLimitState.until - Date.now();
    if (remaining <= 0) {
      return forward(operation);
    }

    return new Observable(observer => {
      let forwardSub: { unsubscribe(): void } | undefined;
      let timer: ReturnType<typeof setTimeout> | undefined;
      let cancelled = false;

      const scheduleExecution = () => {
        const delay = rateLimitState.until - Date.now();
        if (delay <= 0) {
          try {
            forwardSub = forward(operation).subscribe({
              next: v => observer.next(v),
              error: e => observer.error(e),
              complete: () => observer.complete(),
            });
          } catch (e) {
            observer.error(e);
          }
          return;
        }
        const jitter = Math.random() * COOLDOWN_JITTER_MS;
        timer = setTimeout(() => {
          if (!cancelled) scheduleExecution();
        }, delay + jitter);
      };

      scheduleExecution();

      return () => {
        cancelled = true;
        if (timer !== undefined) clearTimeout(timer);
        forwardSub?.unsubscribe();
      };
    });
  });

  const rateLimitDetectLink = onError(({ networkError }) => {
    const statusCode = (networkError as { statusCode?: number } | null)
      ?.statusCode;
    if (statusCode !== 429) return;

    const headers = (networkError as { response?: Response } | null)?.response
      ?.headers;
    const retryAfterHeader = headers?.get('Retry-After');
    const parsed = retryAfterHeader
      ? parseRetryAfter(retryAfterHeader)
      : undefined;
    const cooldownMs = Math.min(
      Math.max(parsed ?? DEFAULT_COOLDOWN_MS, MIN_COOLDOWN_MS),
      MAX_COOLDOWN_MS,
    );
    rateLimitState.until = Date.now() + cooldownMs;

    console.warn(
      `[Rate limit] Circuit breaker active for ${cooldownMs / 1000}s`,
    );
  });

  const retryLink = new RetryLink({
    attempts: (count, operation: Operation, error: Record<string, unknown>) => {
      if (isOperationType(operation, 'mutation')) {
        return false;
      }

      // 429 Too Many Requests — let the circuit breaker handle backoff
      if (error?.statusCode === 429) {
        return false;
      }

      return count <= 3;
    },
    delay: (count: number) => {
      const baseDelay = RETRY_BASE_DELAY_MS * Math.pow(2, count - 1);
      return baseDelay + Math.random() * baseDelay * 0.5;
    },
  });

  // Measure the time it takes to complete an operation
  // and send it to analytics
  const roundTripLink = new ApolloLink((operation, forward) => {
    const definition = getMainDefinition(operation.query);

    operation.setContext({ start: performance.now() });

    // Don't track subscriptions for page load - they're long-lived
    const isSubscription =
      definition.kind === 'OperationDefinition' &&
      definition.operation === 'subscription';
    if (!isSubscription) {
      pageLoadTracker.onRequestStart();
    }

    return forward(operation).map(data => {
      if (!isSubscription) {
        pageLoadTracker.onRequestEnd();

        const pageLoadId = pageLoadTracker.getPageLoadId();
        const time = performance.now() - operation.getContext().start;
        const tracker = resolveAnalytics();
        if (tracker) {
          tracker.captureEvent(
            'graphql-request-timing',
            operation.operationName,
            {
              value: time,
              attributes: {
                requestId: operation.getContext().requestId,
                path: pageLoadTracker.getPath(),
                ...(pageLoadId ? { pageLoadId } : {}),
              },
            },
          );
        }
      }
      return data;
    });
  });

  // The split function takes three parameters:
  //
  // * A function that's called for each operation to execute
  // * The Link to use for an operation if the function returns a "truthy" value
  // * The Link to use for an operation if the function returns a "falsy" value
  const splitLink = split(
    ({ query }) => {
      const definition = getMainDefinition(query);
      return (
        definition.kind === 'OperationDefinition' &&
        definition.operation === 'subscription'
      );
    },
    wsLink,
    httpLink,
  );

  const client = new ApolloClient({
    connectToDevTools: true,
    defaultOptions: {
      watchQuery: {
        fetchPolicy: 'cache-and-network',
        skipPollAttempt: () => document.hidden,
        errorPolicy: 'all',
      },
    },
    link: from([
      ...(onBackendWrite ? [createBackendWriteLink(onBackendWrite)] : []),
      requestIdLink,
      authLink,
      omitTypenameLink,
      rateLimitGateLink,
      errorLink,
      rateLimitDetectLink,
      retryLink,
      roundTripLink,
      splitLink,
    ]),
    cache: new InMemoryCache({
      typePolicies: {
        AdminV1AccountInfo: {
          keyFields: false,
          merge(existing, incoming, { mergeObjects }) {
            return mergeObjects(existing, incoming);
          },
        },
        WorkflowV1Workflow: {
          keyFields: ['name'],
        },
        WorkflowV1WorkflowInstance: {
          keyFields: ['name'],
          fields: {
            checks: {
              merge(_existing, incoming) {
                return incoming;
              },
            },
            moduleData2: {
              merge: true,
            },
          },
        },
        AudiencesDataAudience: {
          // prevent normalization
          keyFields: false,
        },
        WorkflowV1WorkflowVersion: {
          keyFields: ['name'],
          fields: {
            workflowInfo: {
              merge: true,
            },
          },
        },
        IamV1OAuthApp: {
          keyFields: ['name'],
        },
        IamV1Client: {
          keyFields: ['name'],
        },
        RecordingV1RecordingPolicy: {
          keyFields: ['name'],
        },
        RecordingV1RecordingRule: {
          keyFields: ['name'],
        },
        IamV1ApiClient: {
          keyFields: ['name'],
        },
        IamV1Identity: {
          keyFields: ['name'],
        },
        IamV1Group: {
          keyFields: ['name'],
        },
        IamV1CryptoKey: {
          keyFields: ['name'],
        },
        IamV1Policy: {
          keyFields: ['name'],
        },
        IamV1Role: {
          keyFields: ['name'],
        },
        IamV1Agent: {
          keyFields: ['name'],
          fields: {
            reviewerSkill: {
              merge(_existing, incoming) {
                return incoming;
              },
            },
          },
        },
        IamV1CurrentUser: {
          keyFields: false,
          fields: {
            accountMemberships: {
              merge(
                existing: readonly Reference[] | undefined,
                incoming: readonly Reference[],
              ) {
                return incoming.length === 0 && existing?.length
                  ? existing
                  : incoming;
              },
            },
          },
        },
        OnboardingV1TourPreferences: {
          keyFields: [],
        },
        IamV1CurrentUserAccountMembership: {
          keyFields: ['account'],
        },
        FgaV1ResourcePermission: {
          keyFields: ['name'],
        },
        ConnectorsV1FlagAppliedConnection: {
          keyFields: ['name'],
        },
        WorkflowV1Surface: {
          keyFields: ['name'],
          fields: {
            metricConfig: {
              merge: true,
            },
          },
        },
        WorkflowV1SurfaceIdentityBinding: {
          keyFields: ['name'],
        },
        WorkflowV1Initiative: {
          keyFields: ['name'],
        },
        PulseV1Pulse: {
          keyFields: ['name'],
        },
        PulseV1PulseBlock: {
          keyFields: ['name'],
        },
        CollectionsV1Collection: {
          keyFields: ['name'],
        },
        CollectionsV1CollectionEntry: {
          keyFields: ['name'],
        },
        WorkflowV1ExclusivityGroup: {
          keyFields: ['name'],
        },
        FlagsAdminV1Segment: {
          keyFields: ['name'],
        },
        FlagsAdminV1MaterializedSegment: {
          keyFields: ['name'],
        },
        FlagsAdminV1Environment: {
          keyFields: ['name'],
        },
        FlagsAdminV1ResolverServiceConfig: {
          keyFields: ['name'],
        },
        FlagsAdminV1SegmentAllocation: {
          merge(existing, incoming, { mergeObjects }) {
            return mergeObjects(existing, incoming);
          },
        },
        FlagsTypesV1Targeting: {
          merge(_existing, incoming) {
            return incoming;
          },
        },
        FlagsAdminV1Flag: {
          keyFields: ['name'],
        },
        WorkflowV1Holdback: {
          keyFields: ['name'],
        },
        MetricsV1Metric: {
          keyFields: ['name'],
          fields: {
            varianceReductionConfig: {
              merge: true,
            },
            measurementConfig: {
              merge(existing, incoming, { mergeObjects }) {
                const __typename = incoming?.__typename;
                if (incoming?.openWindow) {
                  return {
                    __typename,
                    openWindow: mergeObjects(
                      existing?.openWindow,
                      incoming.openWindow,
                    ),
                    semiOpenWindow: null,
                    closedWindow: null,
                  };
                }
                if (incoming?.closedWindow) {
                  return {
                    __typename,
                    closedWindow: mergeObjects(
                      existing?.closedWindow,
                      incoming.closedWindow,
                    ),
                    openWindow: null,
                    semiOpenWindow: null,
                  };
                }
                if (incoming?.semiOpenWindow) {
                  return {
                    __typename,
                    semiOpenWindow: mergeObjects(
                      existing?.semiOpenWindow,
                      incoming.semiOpenWindow,
                    ),
                    openWindow: null,
                    closedWindow: null,
                  };
                }
                // This should not happen but we need to return something
                return incoming;
              },
            },
          },
        },
        MetricsV1AssignmentTable: {
          keyFields: ['name'],
        },
        MetricsV1DataWarehouse: {
          keyFields: ['name'],
        },
        MetricsV1DimensionTable: {
          keyFields: ['name'],
        },
        MetricsV1FactTable: {
          keyFields: ['name'],
        },
        MetricsV1Partition: {
          keyFields: ['name'],
        },
        MetricsV1EntityRelationTable: {
          keyFields: ['name'],
        },
        MetricsV1Entity: {
          keyFields: ['name'],
        },
        MetricsV1EntityIdentityBinding:
          metricsV1EntityIdentityBindingTypePolicy,
        EventsAdminV1EventDefinition: {
          keyFields: ['name'],
        },
        FlagsAdminV1FlagRule: {
          keyFields: ['name'],
        },
        FlagsAdminV1FlagVariant: {
          keyFields: ['name'],
        },
        EventconnectorsV1Event: {
          keyFields: ['name'],
        },
        MetricsV1ExposureCalculation: {
          keyFields: ['name'],
        },
        MetricsV1Measurement: {
          keyFields: ['name'],
        },
        MetricsV1MeasurementCalculation: {
          keyFields: ['name'],
        },
        MetricsV1MeasurementTable: {
          keyFields: ['name'],
        },
        MetricsV1Retrigger: {
          keyFields: ['name'],
        },
        MetricsV1MetricRatioMetricSpec: {
          keyFields: false,
          merge: true,
        },
        MetricsV1ScheduledExposureCalculation: {
          keyFields: ['name'],
          fields: {
            exposureCalculations: {
              keyArgs: ['parent', 'filter'],
              merge: namedMerger('exposureCalculations'),
            },
            status: {
              merge(_existing, incoming) {
                return incoming;
              },
            },
          },
        },
        MetricsV1ScheduledMeasurementCalculation: {
          keyFields: ['name'],
          fields: {
            measurementCalculations: {
              keyArgs: ['parent', 'filter'],
              merge: namedMerger('measurementCalculations'),
            },
          },
        },
        MetricsV1ScheduledMetricCalculation: {
          keyFields: ['name'],
          fields: {
            metricCalculations: {
              keyArgs: ['parent', 'filter'],
              merge: namedMerger('metricCalculations'),
            },
          },
        },
        AdminLabelsV1Label: {
          keyFields: ['name'],
        },
        ActivityV1Feed: {
          keyFields: ['name'],
        },
        ActivityV1FeedEntry: {
          keyFields: ['name'],
          merge: true,
        },
        ActivityV1Activity: {
          keyFields: ['name'],
          merge: true,
        },
        MetricsV1MetricCalculation: {
          keyFields: ['name'],
        },
        IamV1User: {
          keyFields: ['name'],
        },
        IamV1UserInvitation: {
          keyFields: ['name'],
        },
        CollaborationV1CollaborativeDocument: {
          keyFields: ['name'],
        },
        CollaborationV1ExternalDocument: {
          keyFields: ['name'],
        },
        CollaborationV1Attachment: {
          keyFields: ['name'],
        },
        CollaborationV1Presence: {
          keyFields: ['name'],
        },
        CollaborationV1Comment: {
          keyFields: ['name'],
          fields: {
            commentReplies: {
              keyArgs: ['filter'],
              merge: namedMerger('commentReplies'),
            },
          },
        },
        CollaborationV1CommentReply: {
          keyFields: ['name'],
        },
        CollaborationV1ReactionSummary: {
          keyFields: ['name'],
          fields: {
            emojiReactions: {
              merge(_existing, incoming) {
                return incoming;
              },
            },
          },
        },
        CollaborationV1Reaction: {
          keyFields: ['name'],
        },
        CollaborationV1AgentReviewPreview: {
          keyFields: ['name'],
        },
        CollaborationV1Project: {
          keyFields: ['name'],
        },
        InsightsV1InsightsDocument: {
          keyFields: ['name'],
        },
        InsightsV1InsightsTableContext: {
          keyFields: ['name'],
        },
        InsightsV1SuggestedQuestion: {
          keyFields: false,
        },
        ConversationsV1Conversation: {
          keyFields: ['name'],
        },
        ConversationsV2Chat: {
          keyFields: ['name'],
        },
        ConversationsV2ChatEvent: {
          keyFields: ['name'],
        },
        ConversationsV2PromptHook: {
          keyFields: ['name'],
        },
        AbTestTreatment: {
          keyFields: ['name'],
        },
        AiworkflowsV1AiWorkflowInstance: {
          keyFields: ['name'],
        },
        AiworkflowsV1AiWorkflowSchedule: {
          keyFields: ['name'],
        },
        AiworkflowsV1AiWorkflowTrigger: {
          keyFields: ['name'],
        },
        StatsV2AnalysisAnalysisResult: {
          keyFields: ['name'],
        },
        StatsV2AnalysisStatsHypothesisResult: {
          keyFields: false,
        },
        StatsV2AnalysisStatsGroupStats: {
          keyFields: false,
        },
        StatsV2AnalysisTreatmentInfo: {
          keyFields: false,
        },
        ObservationsV1Observation: {
          keyFields: ['name'],
        },
        ObservationsV1Issue: {
          keyFields: ['name'],
        },
        ObservationsV1Task: {
          keyFields: ['name'],
        },
        // https://www.apollographql.com/docs/react/pagination/core-api/#merging-paginated-results
        Query: {
          fields: {
            activities: {
              keyArgs: ['resource', 'filter'],
              merge: namedMerger('activities'),
            },
            currentUser: {
              keyArgs: false,
              read: e => e,
              merge: (existing, incoming, { mergeObjects }) => {
                return mergeObjects(existing, incoming);
              },
            },
            workflowInstances: {
              keyArgs: ['parent', 'filter', 'orderBy'],
              merge: namedMerger('workflowInstances'),
            },
            holdbacks: {
              keyArgs: ['parent', 'filter'],
              merge: namedMerger('holdbacks'),
            },
            surfaces: {
              keyArgs: ['filter', 'orderBy'],
              merge: namedMerger('surfaces'),
            },
            surfaceIdentityBindings: {
              keyArgs: ['parent'],
              merge: namedMerger('surfaceIdentityBindings'),
            },
            exclusivityGroups: {
              keyArgs: ['parent', 'filter'],
              merge: namedMerger('exclusivityGroups'),
            },
            recordings: {
              keyArgs: ['filter'],
              merge: namedMerger('recordings'),
            },
            collections: {
              keyArgs: ['filter', 'orderBy'],
              merge: namedMerger('collections'),
            },
            collectionEntries: {
              keyArgs: ['parent', 'filter'],
              merge: namedMerger('collectionEntries'),
            },
            insightsDocuments: {
              keyArgs: [],
              merge: namedMerger('insightsDocuments'),
            },
            issues: {
              keyArgs: ['filter', 'orderBy'],
              merge: namedMerger('issues'),
            },
            tasks: {
              keyArgs: ['filter', 'orderBy'],
              merge: namedMerger('tasks'),
            },
            task: {
              keyArgs: ['name'],
              read(_existing, { args, toReference }) {
                if (!args?.name) {
                  return undefined;
                }

                return toReference({
                  __typename: 'ObservationsV1Task',
                  name: args.name,
                });
              },
            },
            pulseChanges: {
              keyArgs: ['parent'],
              merge: namedMerger('pulseChanges'),
            },
            pulses: {
              keyArgs: ['filter'],
              merge: namedMerger('pulses'),
            },
            conversations: {
              keyArgs: [],
              merge: namedMerger('conversations'),
            },
            chats: {
              keyArgs: ['filter', 'orderBy'],
              merge: namedMerger('chats'),
            },
            chatEvents: {
              keyArgs: ['parent', 'filter', 'orderBy'],
              merge: namedMerger('chatEvents'),
            },
            workflows: {
              keyArgs: [],
              merge: namedMerger('workflows'),
            },
            workflowLogs: {
              keyArgs: ['parent'],
              merge: namedMerger('workflowLogs'),
            },
            workflowVersions: {
              keyArgs: ['parent'],
              merge: namedMerger('workflowVersions'),
            },
            oAuthApps: {
              keyArgs: [],
              merge: namedMerger('oAuthApps'),
            },
            partitions: {
              keyArgs: ['parent'],
              merge: namedMerger('partitions'),
            },
            processRoundLogs: {
              keyArgs: ['parent'],
              merge: namedMerger('processRoundLogs'),
            },
            measurements: {
              keyArgs: ['filter', 'orderBy'],
              merge: namedMerger('measurements'),
            },
            metrics: {
              keyArgs: ['filter', 'orderBy'],
              merge: namedMerger('metrics'),
            },
            flags: {
              keyArgs: ['filter', 'orderBy'],
              merge: namedMerger('flags'),
            },
            apiClients: {
              keyArgs: ['filter', 'orderBy'],
              merge: namedMerger('apiClients'),
            },
            agents: {
              keyArgs: ['filter'],
              merge: namedMerger('agents'),
            },
            identities: {
              keyArgs: ['filter'],
              merge: namedMerger('identities'),
            },
            labels: {
              keyArgs: ['filter'],
              merge: namedMerger('labels'),
            },
            groups: {
              keyArgs: ['filter'],
              merge: namedMerger('groups'),
            },
            clients: {
              keyArgs: ['filter', 'orderBy'],
              merge: namedMerger('clients'),
            },
            segments: {
              keyArgs: ['filter', 'view', 'orderBy'],
              merge: namedMerger('segments'),
            },
            materializedSegments: {
              keyArgs: ['filter'],
              merge: namedMerger('materializedSegments'),
            },
            resolverServiceConfigs: {
              keyArgs: [],
              merge: namedMerger('resolverServiceConfigs'),
            },
            materializedSegment: {
              keyArgs: ['name'],
              merge(existing, incoming, { mergeObjects }) {
                return mergeObjects(existing, incoming);
              },
            },
            clientCredentials: {
              keyArgs: ['parent'],
              merge: namedMerger('clientCredentials'),
            },
            eventDefinitions: {
              keyArgs: ['filter', 'orderBy'],
              merge: namedMerger('eventDefinitions'),
            },
            metricCalculations: {
              keyArgs: ['parent', 'filter'],
              merge: namedMerger('metricCalculations'),
            },
            entities: {
              keyArgs: ['filter', 'orderBy'],
              merge: namedMerger('entities'),
            },
            entityIdentityBindings: entityIdentityBindingsListFieldPolicy,
            assignmentTables: {
              keyArgs: ['filter', 'orderBy'],
              merge: namedMerger('assignmentTables'),
            },
            factTables: {
              keyArgs: ['filter', 'orderBy'],
              merge: namedMerger('factTables'),
            },
            dimensionTables: {
              keyArgs: ['filter', 'orderBy'],
              merge: namedMerger('dimensionTables'),
            },
            exposureCalculations: {
              keyArgs: ['parent'],
              merge: namedMerger('exposureCalculations'),
            },
            measurementCalculations: {
              keyArgs: ['parent', 'filter'],
              merge: namedMerger('measurementCalculations'),
            },
            events: {
              keyArgs: ['resource'],
              merge: namedMerger('events'),
            },
            resourcePermissions: {
              keyArgs: ['resource'],
              merge: namedMerger('resourcePermissions'),
            },
            scheduledExposureCalculations: {
              keyArgs: [],
              merge: namedMerger('scheduledExposureCalculations'),
            },
            scheduledMetricCalculations: {
              keyArgs: [],
              merge: namedMerger('scheduledMetricCalculations'),
            },
            users: {
              keyArgs: ['filter', 'orderBy'],
              merge: namedMerger('users'),
            },
            groupMembers: {
              keyArgs: ['parent', 'filter'],
              merge: namedMerger('groupMembers'),
            },
            userInvitations: {
              keyArgs: [],
              merge: namedMerger('userInvitations'),
            },
            policies: {
              keyArgs: [],
              merge: namedMerger('policies'),
            },
            permissions: {
              keyArgs: [],
              merge(
                existing: { permissions?: unknown[] } | undefined,
                incoming: { permissions?: unknown[] } & Record<string, unknown>,
              ) {
                if (!existing?.permissions) return incoming;
                return {
                  ...incoming,
                  permissions: [
                    ...existing.permissions,
                    ...(incoming?.permissions ?? []),
                  ],
                };
              },
            },
            roles: {
              keyArgs: [],
              merge: namedMerger('roles'),
            },
            presences: {
              keyArgs: [],
              merge: namedMerger('presences'),
            },
            initiatives: {
              keyArgs: ['filter', 'orderBy'],
              merge: namedMerger('initiatives'),
            },
            projects: {
              keyArgs: ['filter', 'orderBy'],
              merge: namedMerger('projects'),
            },
            comments: {
              keyArgs: ['filter', 'resource'],
              merge: namedMerger('comments'),
            },
            commentReplies: {
              keyArgs: ['parent'],
              merge: namedMerger('commentReplies'),
            },
            existsDataWarehouses: {
              keyArgs: false,
              merge(existing, incoming, { mergeObjects }) {
                return mergeObjects(existing, incoming);
              },
            },
            feeds: {
              keyArgs: [],
              merge: namedMerger('feeds'),
            },
            feedEntries: {
              keyArgs: ['parent'],
              merge: namedMerger('feedEntries'),
            },
            aiWorkflowTriggers: {
              keyArgs: ['filter'],
              merge: namedMerger('aiWorkflowTriggers'),
            },
            queryCreditUsage: {
              keyArgs: ['startTime', 'endTime', 'usageFeature', 'pageSize'],
              merge(
                existing: { lines?: unknown[] } | undefined,
                incoming: { lines?: unknown[] } & Record<string, unknown>,
                { args }: { args: Record<string, unknown> | null },
              ) {
                if (!existing?.lines || !args?.pageToken) return incoming;
                return {
                  ...incoming,
                  lines: [...existing.lines, ...(incoming?.lines ?? [])],
                };
              },
            },
          },
        },
      },
    }),
  });

  const stopClient = client.stop.bind(client);
  client.stop = () => {
    stopClient();
    wsClient.dispose();
  };

  operationQueueManager.setReadyClient(client);

  if (operationQueueManager.hasQueuedOperations()) {
    setTimeout(() => operationQueueManager.processQueue(), 0);
  }

  return client;
};
